import React, { useEffect, useState } from 'react';
import axios from '@/utils/axiosInstance';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { FaFileExport } from "react-icons/fa";
import { ChevronsLeft, ChevronsRight } from 'lucide-react';

interface PutawayTask {
  id: string;
  createdAt: string;
  qty: number;
  status: 'PENDING' | 'COMPLETED' | 'CANCELLED';
  product?: { name: string };
  suggestedLocation?: { name: string };
  actualLocation?: { name: string };
  inboundOrderDetail?: {
    id: string;
    inboundOrder?: {
      inboundOrderNo: string;
    };
  };
}

interface SelectedTask {
  locationId: string;
  inboundOrderDetailId: string;
}

const PutawayOutstandingList = () => {
  const [tasks, setTasks] = useState<PutawayTask[]>([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [locations, setLocations] = useState<{ id: string; name: string }[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<{ [taskId: string]: SelectedTask }>({});

  const { logout } = useAuth();
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL;

  const getAuthHeader = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}` || '',
      'Content-Type': 'application/json',
    },
  });

  const fetchTasks = async () => {
    try {
      const res = await axios.get(`${API}/putaway-tasks`, {
        ...getAuthHeader(),
        params: {
          page: currentPage,
          limit: rowsPerPage,
          search,
          status: 'PENDING',
          sortOrder: 'ASC',
        },
      });
      setTasks(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err: any) {
      if (err.response?.status === 401) {
        logout();
        navigate('/login');
      }
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API}/locations`, getAuthHeader());
      setLocations(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch locations');
    }
  };

  const updateSelectedTask = (taskId: string, locationId: string, inboundOrderDetailId: string) => {
    setSelectedTasks((prev) => ({
      ...prev,
      [taskId]: { locationId, inboundOrderDetailId },
    }));
  };

const handleSubmitBulk = async () => {
  // Filter task yang lengkap (lokasi + inboundOrderDetailId)
  const validEntries = Object.entries(selectedTasks).filter(
    ([_, v]) =>
      typeof v === 'object' &&
      v.locationId &&
      v.inboundOrderDetailId
  );

  if (validEntries.length === 0) {
    alert('Choose task and location before submit.');
    return;
  }

  if (!window.confirm('Are you sure you want to submit this data?')) return;

  try {
    for (const [taskId, value] of validEntries) {
      if (typeof value !== 'object') continue;

      const { locationId, inboundOrderDetailId } = value;

      // DEBUG: cek payload sebelum dikirim
      

      await axios.post(
        `${API}/putaway-tasks/${taskId}/submit`,
        {
          actual_location_id: locationId,
          inbound_order_detail_id: inboundOrderDetailId,
        },
        getAuthHeader()
      );
    }

    alert('Location saved.');
    setSelectedTasks({});
    fetchTasks();
  } catch (err: any) {
    console.error('Failed saving location:', err.response || err);
    alert('Failed saving location. Check console for details.');
  }
};


  const handleExport = () => {
    const exportData = tasks.map((task) => ({
      'Inbound Order No': task.inboundOrderDetail?.inboundOrder?.inboundOrderNo || '-',
      'Inbound Order Date': dayjs(task.createdAt).format('DD/MM/YYYY'),
      'Product': task.product?.name || '-',
      'Default Location': task.suggestedLocation?.name || '-',
      'Qty': task.qty,
      'Actual Location': task.actualLocation?.name || '-',
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'OutstandingPutaway');
    XLSX.writeFile(workbook, 'OutstandingPutaway.xlsx');
  };

  useEffect(() => { fetchTasks(); }, [currentPage, rowsPerPage]);
  useEffect(() => {
    const timeout = setTimeout(() => {
      setCurrentPage(1);
      fetchTasks();
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);
  useEffect(() => { fetchLocations(); }, []);

  const maxPage = Math.ceil(total / rowsPerPage);

  return (
    <div className="p-1 mx-auto max-w-6xl">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Putaway - Outstanding</h2>
          <button
            onClick={handleSubmitBulk}
            className="bg-green-600 hover:bg-green-700 text-white text-[13px] px-3 py-1 rounded disabled:opacity-50"
            disabled={Object.values(selectedTasks).filter(Boolean).length === 0}
          >
            Process Selected
          </button>
        </div>

        {/* Filter & Table */}
        <div className="overflow-x-auto border rounded mb-2">
          <table className="min-w-full text-[13px]">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-2 py-2 text-center">✔</th>
                <th className="border px-2 py-1 text-center">Inbound Order No</th>
                <th className="border px-2 py-1 text-center">Inbound Order Date</th>
                <th className="border px-2 py-1 text-center">Product</th>
                <th className="border px-2 py-1 text-center">Default Location</th>
                <th className="border px-2 py-1 text-center">Qty</th>
                <th className="border px-2 py-1 text-center">Actual Location</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => (
                <tr key={task.id} className="hover:bg-gray-50">
                  <td className="border px-2 py-1 text-center">
                    <input
                      type="checkbox"
                      disabled={task.status !== 'PENDING'}
                      checked={task.id in selectedTasks}
                      onChange={(e) => {
                        const updated = { ...selectedTasks };
                        if (!e.target.checked) delete updated[task.id];
                        else
                          updated[task.id] = {
                            locationId: '',
                            inboundOrderDetailId: task.inboundOrderDetail?.id || '',
                          };
                        setSelectedTasks(updated);
                      }}
                    />
                  </td>
                  <td className="border px-2 py-1">{task.inboundOrderDetail?.inboundOrder?.inboundOrderNo || '-'}</td>
                  <td className="border px-2 py-1">{dayjs(task.createdAt).format('DD/MM/YYYY h:m')}</td>
                  <td className="border px-2 py-1">{task.product?.name || '-'}</td>
                  <td className="border px-2 py-1">{task.suggestedLocation?.name || '-'}</td>
                  <td className="border px-2 py-1 text-right">{task.qty}</td>
                  <td className="border px-2 py-1">
                    {task.id in selectedTasks ? (
                      <select
                        className="text-[13px] border rounded px-1 py-0.5"
                        value={selectedTasks[task.id].locationId}
                        onChange={(e) =>
                          updateSelectedTask(task.id, e.target.value, selectedTasks[task.id].inboundOrderDetailId)
                        }
                      >
                        <option value="">Pilih Lokasi</option>
                        {locations.map((loc) => (
                          <option key={loc.id} value={loc.id}>{loc.name}</option>
                        ))}
                      </select>
                    ) : task.actualLocation?.name || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-4 text-[13px] flex-wrap gap-2">
          <div>
            Showing {(currentPage - 1) * rowsPerPage + 1}–{Math.min(currentPage * rowsPerPage, total)} of {total}
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <button
              className="px-2 py-1 border rounded disabled:opacity-30 text-gray-600 hover:bg-gray-100"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(1)}
            >
              <ChevronsLeft size={18} />
            </button>
            <button
              className="px-2 py-1 border rounded disabled:opacity-30 text-gray-600 hover:bg-gray-100"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            >
              «
            </button>
            {Array.from({ length: maxPage }, (_, i) => i + 1)
              .filter((page) => Math.abs(page - currentPage) <= 2)
              .map((page) => (
                <button
                  key={page}
                  className={`px-2 py-1 border rounded ${
                    page === currentPage
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
            <button
              className="px-2 py-1 border rounded disabled:opacity-30 text-gray-600 hover:bg-gray-100"
              disabled={currentPage === maxPage}
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, maxPage))}
            >
              »
            </button>
            <button
              className="px-2 py-1 border rounded disabled:opacity-30 text-gray-600 hover:bg-gray-100"
              disabled={currentPage === maxPage}
              onClick={() => setCurrentPage(maxPage)}
              title="Last Page"
            >
              <ChevronsRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PutawayOutstandingList;
